import React, { useState } from 'react';
import PageHero from './PageHero';
import { BookOpen, Sparkles, FileText, ArrowRight } from 'lucide-react';
import BookReader from './BookReader';

export default function NewsletterPage({ setPage, t }) {
  const [selectedPdf, setSelectedPdf] = useState(null);

  const navCategory = t?.nav?.[4]?.label || "News & Events";
  const homeLabel = t?.nav?.[0]?.label || "Home";

  const newsletters = [
    {
      id: 1,
      title: "July 2026 Edition",
      subtitle: "School Opening & Training",
      desc: "Dive into our exciting start of the academic year. This edition covers staff training sessions, engaging student assemblies, and outstanding academic achievements.",
      pdfUrl: `${window.location.origin}/sample.pdf`,
      status: "available",
      date: "July 2026",
      image: "https://placehold.co/345x195"
    },
    {
      id: 2,
      title: "June 2026 Edition",
      subtitle: "End of Year Review",
      desc: "A comprehensive look back at the accomplishments of the past academic year, featuring graduation highlights, final project showcases, and student awards.",
      pdfUrl: `${process.env.PUBLIC_URL}/sample.pdf`,
      status: "available",
      date: "June 2026",
      image: "https://placehold.co/345x195"
    },
    {
      id: 3,
      title: "August 2026 Edition",
      subtitle: "Upcoming Events",
      desc: "Stay tuned for the next edition of our school newsletter with more updates, events, and community achievements.",
      pdfUrl: null,
      status: "upcoming",
      date: "August 2026",
      image: "https://placehold.co/345x195"
    }
  ];

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen pb-32 l ">
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <PageHero
        title={"School Newsletters"}
        breadcrumbs={[homeLabel, navCategory, "Newsletters"]}
        eyebrow={navCategory}
        subtitle={"Stay updated with the latest news, events, and achievements at GMIS Jakarta"}
      />


      {/* ── Main Content ── */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 pt-16 lg:pt-[300px]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {newsletters.map((item) => (
            <div
              key={item.id}
              onClick={() => {
                if (item.status === 'available' && item.pdfUrl) {
                  setSelectedPdf(item);
                }
              }}
              className={`bg-white rounded-[20px] overflow-hidden shadow-[0_8px_30px_rgba(14,36,62,0.04)] border border-line flex flex-col group ${item.status === 'available' ? 'cursor-pointer hover:shadow-[0_20px_50px_rgba(14,36,62,0.1)] hover:-translate-y-1 transition-all duration-300' : 'opacity-80 cursor-not-allowed'}`}
            >
              {/* Image Area */}
              <div className="relative h-48 bg-navy/5 overflow-hidden shrink-0">
                <div className={`absolute inset-0 transition-colors duration-500 z-10 ${item.status === 'available' ? 'bg-navy/10 group-hover:bg-transparent' : 'bg-navy/5'}`} />
                <img
                  src={item.image}
                  alt={item.title}
                  className={`w-full h-full object-cover object-center ${item.status === 'available' ? 'group-hover:scale-105 transition-transform duration-700' : ''}`}
                />
                <div className="absolute top-5 left-5 z-20">
                  <span className="bg-white/95 backdrop-blur text-navy text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full shadow-sm">
                    {item.date}
                  </span>
                </div>
                {item.status === 'available' && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 pointer-events-none">
                    <div className="bg-navy/80 text-white rounded-full p-4 backdrop-blur shadow-xl">
                      <FileText size={32} />
                    </div>
                  </div>
                )}
              </div>

              {/* Content Area */}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-gold font-bold text-xs uppercase tracking-wider mb-3">
                  <BookOpen size={14} />
                  {item.status === 'available' ? 'Latest Edition' : 'Coming Soon'}
                </div>

                <h3 className={`text-2xl font-heading font-semibold mb-2 leading-tight transition-colors duration-300 ${item.status === 'available' ? 'text-navy group-hover:text-gold' : 'text-navy/60'}`}>
                  {item.title}
                </h3>

                <h4 className="text-[15px] font-semibold text-navy/70 mb-4">{item.subtitle}</h4>

                <p className="text-ink/70 leading-[1.7] text-[15px] mb-8 flex-1">
                  {item.desc}
                </p>

                <div className="mt-auto border-t border-line/60 pt-5">
                  <span className={`inline-flex items-center gap-2 font-bold text-[13px] tracking-wider uppercase transition-colors duration-300 ${item.status === 'available' ? 'text-gold group-hover:text-navy' : 'text-ink/40'}`}>
                    {item.status === 'available' ? 'Read Newsletter' : 'Not Available Yet'}
                    {item.status === 'available' && <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Book Reader Modal ── */}
      {selectedPdf && (
        <BookReader
          pdfUrl={selectedPdf.pdfUrl}
          title={selectedPdf.title}
          subtitle={selectedPdf.subtitle}
          onClose={() => setSelectedPdf(null)}
        />
      )}
    </div>
  );
}
